/**
 * # i18n service
 *
 * > @see /apps/shared/doc/i18n.md
 *
 * Today use @ngx-translate which is deprecated
 * So we may change lib in near furture
 *
 * That's why we create a service to wrap it - so if we swap engine this interface will remain the same
 */
import type { Signal } from '@angular/core';
import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, Observable, shareReplay } from 'rxjs';

import type {
  EvcI18nService,
  I18nConfig,
  TabletradMapping,
  TranslationKey,
  TranslationParams,
} from '@evc/platform';

import { SharedConfigService } from '../config/config.service';
import type { LangChangeEvent } from './i18n.module';
import { TranslateService } from './i18n.module';

/** 🤯 @ngx-translate does not provide a standalone pipe so must export the module
 * Besause we expect to swap i18n engine, we desguise this issue to be compliant with future implementation
 * Also, because this is a facade - importing any dependency from here will remove dependencies accross all codebase
 */
export { TranslatePipe, TranslateLoader, TranslateHttpLoader } from './i18n.module';

@Injectable({
  providedIn: 'root',
})
export class I18nService implements EvcI18nService {
  translateService = inject(TranslateService);
  #destroyRef = inject(DestroyRef);
  #config: I18nConfig = inject(SharedConfigService).get('i18n');

  onLangChange$ = new Observable<LangChangeEvent>((observer) => {
    this.translateService.onLangChange.subscribe((event) => observer.next(event));
  });
  langReady = toSignal(this.onLangChange$);
  onReady$ = this.onLangChange$.pipe(
    takeUntilDestroyed(this.#destroyRef),
    filter((event: LangChangeEvent) => event && event.lang === this.lang),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  get lang(): string {
    return this.translateService.currentLang;
  }

  setLanguage(language: string): void {
    this.translateService.use(language);
  }

  /** instant translate (not reactive - be sure it is ready) */
  t(key: TranslationKey, interpolateParams?: TranslationParams): string {
    const message = this.translateService.instant(key, interpolateParams);

    return interpolateParams?.p11n
      ? this.p11n(message, interpolateParams?.p11n as number)
      : message;
  }
  /** translate using Signal (reactive - use with `const text = tSignal('text') ; then use text()`) */
  tSignal(key: TranslationKey, interpolateParams?: TranslationParams): Signal<string> {
    return computed(() => {
      this.langReady();

      return this.t(key, interpolateParams);
    });
  }
  /** translate using Observable (reactive - use with `| optionalAsync`) */
  t$(key: TranslationKey, interpolateParams?: TranslationParams): Observable<string> {
    return this.translateService
      .stream(key, interpolateParams)
      .pipe(
        map((message: string) =>
          interpolateParams?.p11n ? this.p11n(message, interpolateParams?.p11n as number) : message,
        ),
      );
  }

  /** simple pluralization
   * - support 2 forms (singular|plural) or 3 forms (zero|singular|plural)
   * - simply add | to your message `cars: nothing|1 car|{count} cars`
   *    - if 1 pipe : single|plural
   *    - if 2 pipes : zero|single|plural
   *
   * ! This simply splint a string
   * You may like to use special `p11n` param within t/t$/tSignal()
   *
   * ```
   * this.t('key.to.localize', {
   *   p11n: count, // pluralization special param
   *   attr: 'dynamic attribute', // all others will just be replaced dynamically
   * })
   */
  p11n(text: string, count: number): string {
    const splitted = text.split('|');

    let index = count;
    if (splitted.length === 3) {
      index = Math.min(count, 2);

      return splitted[index].trim();
    } else {
      index = count > 1 ? 1 : 0;

      return splitted[index].trim();
    }
  }

  constructor() {
    const { languages, lang } = this.#config;

    this.translateService.addLangs(languages);
    if (lang) this.translateService.setDefaultLang(lang);
  }

  createTableColumnsMapping(columnsKeys: string[], tradScope: string): TabletradMapping {
    const translatedKeys = columnsKeys.map((key) => this.t(`${tradScope}.${key}`) ?? key);

    const columnsMapping = columnsKeys.reduce(
      (acc, key, index) => {
        acc[translatedKeys[index]] = key;

        return acc;
      },
      {} as Record<string, string>,
    );

    return { translatedKeys, columnsMapping };
  }
}
